import { useCallback } from 'react';
import {
  AddOnOptionSelection,
  AddOnType,
  MemberRegistrationType,
  Product,
  UserSelection,
} from '@la/types';
import { getCaseInsensitiveStringMatch } from '@la/utilities';
import {
  selectAddOnOptionSelections,
  selectAddOnProducts,
  selectRegistrationType,
  selectTaxRate,
  selectSelectionsWithErrors,
} from 'redux/selectors/registrationAddOnsSelectors';
import { calculatePrice } from 'domains/MemberRegistration/AddOnCard/AddOnPriceCalculator';
import { ProductWithMappedSelections } from 'domains/MemberRegistration/MemberRegistration.types';
import {
  setAddOnOptionSelections as setAddOnOptionSelectionsAction,
  setRegistrationAddOns as setRegistrationAddOnsAction,
  setRegistrationType as setRegistrationTypeAction,
  setSelectionsWithErrors as setSelectionsWithErrorsAction,
  setTaxRate as setTaxRateAction,
  updateOptionalAddOns as updateOptionalAddOnsAction,
  updateRequiredAddOnSelection as updateRequiredAddOnSelectionAction,
} from '../registrationAddOnsSlice';
import { useAppDispatch, useAppSelector } from '../store';

export const useRegistrationAddOns = () => {
  const dispatch = useAppDispatch();
  const taxRate = useAppSelector(selectTaxRate);

  const addOnProducts: Product[] = useAppSelector(selectAddOnProducts);
  const registrationType: MemberRegistrationType | undefined = useAppSelector(
    selectRegistrationType
  );
  const addOnOptionSelections: AddOnOptionSelection[] = useAppSelector(
    selectAddOnOptionSelections
  );
  const selectionsWithErrors = useAppSelector(selectSelectionsWithErrors);

  const getAddOnsByType = useCallback(
    (type: MemberRegistrationType) => {
      return addOnProducts.filter((addOn) =>
        addOn.assignedRoles.some((role) => role.role === type.toUpperCase())
      );
    },
    [addOnProducts]
  );

  const getAddOns = useCallback(
    (category: AddOnType, type: MemberRegistrationType) => {
      const addOnsByType = getAddOnsByType(type);

      return addOnsByType.filter((addOn) =>
        addOn.assignedRoles.some(
          (role) =>
            (category === 'optional' &&
              !role.required &&
              getCaseInsensitiveStringMatch(role.role, type)) ||
            (category === 'required' &&
              role.required &&
              getCaseInsensitiveStringMatch(role.role, type))
        )
      );
    },
    [getAddOnsByType]
  );

  const getOptionalPlayerAddOns = () => getAddOns('optional', 'player');
  const getOptionalStaffAddOns = () => getAddOns('optional', 'staff');
  const getRequiredPlayerAddOns = () => getAddOns('required', 'player');
  const getRequiredStaffAddOns = () => getAddOns('required', 'staff');

  const getRequiredAddOnsByType = useCallback(
    (type: MemberRegistrationType) => {
      const addOnsByType = getAddOnsByType(type);
      return addOnsByType.filter((addOn) =>
        addOn.assignedRoles.some(
          (role) => role.required && role.role === type.toUpperCase()
        )
      );
    },
    [getAddOnsByType]
  );

  const getOptionalAddOnsByType = (type: MemberRegistrationType) => {
    const addOnsByType = getAddOnsByType(type);
    return addOnsByType.filter((addOn) =>
      addOn.assignedRoles.some(
        (role) => !role.required && role.role === type.toUpperCase()
      )
    );
  };

  const getUserSelectionsByAddOnId = useCallback(
    (productId: number) => {
      const filteredUserSelections = addOnOptionSelections.find(
        (addOnOptionSelection) => addOnOptionSelection.addOnId === productId
      );
      return filteredUserSelections ? filteredUserSelections.selections : [];
    },
    [addOnOptionSelections]
  );

  const setRegistrationAddOns = useCallback(
    (addOns: Product[]) => {
      dispatch(setRegistrationAddOnsAction(addOns));
    },
    [dispatch]
  );

  const setRegistrationType = useCallback(
    (registrationType: string) => {
      const regType = registrationType.toLowerCase().includes('player')
        ? 'player'
        : 'staff';
      dispatch(setRegistrationTypeAction(regType));
    },
    [dispatch]
  );

  const setTaxRate = useCallback(
    (taxRate: number) => {
      dispatch(setTaxRateAction(taxRate));
    },
    [dispatch]
  );

  const setAddOnOptionSelections = useCallback(
    (addOnOptionSelections: AddOnOptionSelection[]) => {
      dispatch(setAddOnOptionSelectionsAction(addOnOptionSelections));
    },
    [dispatch]
  );

  const updateAddOnQuantity = useCallback(
    (productInstanceId: string, quantity: number) => {
      const updatedSelections = addOnOptionSelections.map(
        (addOnOptionSelection) =>
          addOnOptionSelection.productInstanceId === productInstanceId
            ? { ...addOnOptionSelection, quantity }
            : addOnOptionSelection
      );
      dispatch(setAddOnOptionSelectionsAction(updatedSelections));
    },
    [addOnOptionSelections, dispatch]
  );

  const updateRequiredAddOnSelection = useCallback(
    (addOnId: number, propertyId: string, value: string) => {
      dispatch(
        updateRequiredAddOnSelectionAction({ addOnId, propertyId, value })
      );
    },
    [dispatch]
  );

  /* Functions used by getSelectedProducts() */
  const calculateProductPrice = useCallback(
    (product: Product, selections: UserSelection[]): number => {
      let calculatedProductPrice = product.price;

      if (
        product.pricingConfigurations &&
        product.pricingConfigurations.length > 0
      ) {
        calculatedProductPrice = calculatePrice(
          product.price,
          selections,
          product.pricingConfigurations
        );
      }

      return calculatedProductPrice;
    },
    []
  );

  const mapSelectionToProduct = useCallback(
    (selection: AddOnOptionSelection, products: Product[]) => {
      const product = products.find(
        (product) => product.id === selection.addOnId
      );

      if (!product) {
        return null;
      }

      const price = calculateProductPrice(product, selection.selections);
      const subTotal = price * selection.quantity;
      const salesTax =
        product.salesTaxRequired && taxRate
          ? Math.round(subTotal * taxRate) / 100
          : 0;
      const itemCost = subTotal + salesTax;

      return {
        addOnType: selection.addOnType,
        itemCost: itemCost,
        productId: product.id,
        productInstanceId: selection.productInstanceId,
        productName: product.name,
        quantity: selection.quantity,
        salesTax: salesTax,
        salesTaxRequired: product.salesTaxRequired,
        selectionTypes: selection.selections,
        subTotal: subTotal,
        taxRate: taxRate,
      };
    },
    [calculateProductPrice, taxRate]
  );
  /* */

  const getSelectedProduct = useCallback(
    (productId: number) => {
      const selection = addOnOptionSelections.find(
        (selection) => selection.addOnId === productId && selection.quantity > 0
      );

      if (!selection) {
        return null;
      }

      return mapSelectionToProduct(selection, addOnProducts);
    },
    [addOnProducts, mapSelectionToProduct, addOnOptionSelections]
  );

  const getSelectedProducts = useCallback<
    () => ProductWithMappedSelections[]
  >(() => {
    return (
      addOnOptionSelections
        .filter((selection) => selection.quantity > 0)
        .map((selection) => mapSelectionToProduct(selection, addOnProducts))
        // The "item is NonNullable<typeof item>" assertion lets TS know that null values are being removed.
        .filter((item): item is NonNullable<typeof item> => item !== null)
    );
  }, [addOnProducts, mapSelectionToProduct, addOnOptionSelections]);

  const getTotalProductCost = useCallback(() => {
    const selectedProducts = getSelectedProducts();
    const total = selectedProducts.reduce((sum, product) => {
      return sum + product.itemCost;
    }, 0);
    return Math.round(total * 100) / 100;
  }, [getSelectedProducts]);

  const updateOptionalAddOns = (optionalAddOn: AddOnOptionSelection) => {
    const { productInstanceId, quantity } = optionalAddOn;
    const existingAddOn = addOnOptionSelections.find(
      (selection) => selection.productInstanceId === productInstanceId
    );
    let updatedAddOnOptionSelections: AddOnOptionSelection[];

    if (existingAddOn) {
      updatedAddOnOptionSelections = [
        ...addOnOptionSelections.filter(
          (selection) => selection.productInstanceId !== productInstanceId
        ),
        { ...existingAddOn, quantity: existingAddOn.quantity + quantity },
      ];
    } else {
      updatedAddOnOptionSelections = [...addOnOptionSelections, optionalAddOn];
    }

    dispatch(updateOptionalAddOnsAction({ updatedAddOnOptionSelections }));
  };

  const getSelectionsWithErrors = (): string[] => {
    let newErrors: string[] = [];
    const requiredAddOns = getRequiredAddOnsByType(registrationType!);
    // for each required add on in addOnProducts
    for (const requiredAddOn of requiredAddOns) {
      // locate the properties that are required
      for (const property of requiredAddOn.properties ?? []) {
        if (property.required) {
          const matchingSelectionsRecord = addOnOptionSelections.find(
            (addOnOptionSelection) =>
              addOnOptionSelection.addOnId === requiredAddOn.id
          );
          const propertyIdString = property.id.toString();
          const matchingSelection = matchingSelectionsRecord?.selections.find(
            (selection: UserSelection) =>
              selection.optionId === propertyIdString
          );

          if (!matchingSelection) {
            // Add error if matchingSelection doesn't exist
            newErrors.push(propertyIdString);
          }
        }
      }
    }
    return newErrors;
  };

  const validateRequiredAddOns = () => {
    const newErrors = getSelectionsWithErrors();
    dispatch(setSelectionsWithErrorsAction(newErrors));
    return newErrors.length === 0;
  };

  const deleteOptionalAddOn = (productInstanceId: string) => {
    const updatedAddOnOptionSelections = addOnOptionSelections.filter(
      (selection) => selection.productInstanceId !== productInstanceId
    );
    dispatch(updateOptionalAddOnsAction({ updatedAddOnOptionSelections }));
  };

  const removeSelectionWithError = (propertyId: string) => {
    const newErrors = selectionsWithErrors.filter((err) => err !== propertyId);
    dispatch(setSelectionsWithErrorsAction(newErrors));
  };

  return {
    deleteOptionalAddOn,
    getAddOnsByType,
    getOptionalAddOnsByType,
    getRequiredAddOnsByType,
    getRequiredStaffAddOns,
    getOptionalStaffAddOns,
    getRequiredPlayerAddOns,
    getOptionalPlayerAddOns,
    getUserSelectionsByAddOnId,
    getSelectedProduct,
    getSelectedProducts,
    getTotalProductCost,
    removeSelectionWithError,
    setAddOnOptionSelections,
    setRegistrationAddOns,
    setRegistrationType,
    setTaxRate,
    updateAddOnQuantity,
    updateRequiredAddOnSelection,
    updateOptionalAddOns,
    validateRequiredAddOns,
  };
};
